import { createApi } from '@reduxjs/toolkit/query/react';
import type { BaseQueryApi } from '@reduxjs/toolkit/query';
import { createNextState, current } from '@reduxjs/toolkit';
import { baseQueryWithAuth } from '../base-query';

export const NOTIFICATIONS_QUERY_ARGS = {
  unread_only: false,
  limit: 20,
} as const;

export enum NotificationType {
  LOW_CREDITS = 'low_credits',
  CREDITS_DEPLETED = 'credits_depleted',
  ENRICHMENT_COMPLETE = 'enrichment_complete',
  ENRICHMENT_FAILED = 'enrichment_failed',
  SUBSCRIPTION_RENEWED = 'subscription_renewed',
  SUBSCRIPTION_CANCELLED = 'subscription_cancelled',
  PAYMENT_FAILED = 'payment_failed',
  NEW_FEATURE = 'new_feature',
  SYSTEM_MAINTENANCE = 'system_maintenance',
  // CRM sync lifecycle. SYNC_FAILED is emitted by notify_crm_disconnected when a
  // CRM connection drops on the auto-push path (must match the BE
  // NotificationType.sync_failed value exactly).
  SYNC_FAILED = 'sync_failed',
  SYNC_COMPLETED = 'sync_completed',
  GENERAL = 'general',
}

export enum NotificationPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

export interface Notification {
  id: string;
  user_id: string;
  organization_id: string;
  notification_type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  is_read: boolean;
  read_at?: string;
  created_at: string;
  expires_at?: string;
  action_url?: string;
  action_text?: string;
  metadata?: Record<string, unknown>;
}

export interface NotificationPreferences {
  email_low_credits: boolean;
  email_threshold: number;
  in_app_notifications: boolean;
  daily_summary: boolean;
  weekly_summary: boolean;
  drip_emails: boolean;
}

export interface NotificationsListResponse {
  notifications: Notification[];
  unread_count: number;
}

export interface UpdateNotificationPreferencesRequest {
  email_low_credits?: boolean;
  email_threshold?: number;
  in_app_notifications?: boolean;
  daily_summary?: boolean;
  weekly_summary?: boolean;
  drip_emails?: boolean;
}

export interface MarkNotificationsReadRequest {
  notification_ids: string[];
}

export interface MarkNotificationsReadResponse {
  message: string;
  updated_count: number;
}

/*
 * The bell's writes (mark read, mark all read, clear) patch the cached list
 * the moment they start. A list read that overlaps one (the 30 s poll landing
 * mid-request) can be answered from before the write landed, and caching that
 * answer would put back what the write just changed. So writes are tracked
 * from start to settle, and a read that overlapped one waits for the writes to
 * settle, then reads again if any of them landed. RTK Query's invalidation
 * still refetches after every write.
 */
type OptimisticWrite = {
  apply: (draft: NotificationsListResponse) => void;
  failed: boolean;
};
type WriteState = {
  clock: number;
  writesInFlight: number;
  lastWriteSettledAt: number;
  lastWriteLandedAt: number;
  waiters: Set<() => void>;
  batch?: {
    base?: NotificationsListResponse;
    operations: OptimisticWrite[];
  };
};
const writeStates = new WeakMap<object, WriteState>();

function writeState(dispatch: BaseQueryApi['dispatch']): WriteState {
  let state = writeStates.get(dispatch);
  if (!state) {
    state = {
      clock: 0,
      writesInFlight: 0,
      lastWriteSettledAt: 0,
      lastWriteLandedAt: 0,
      waiters: new Set(),
    };
    writeStates.set(dispatch, state);
  }
  return state;
}

// Stop retrying without caching an overlapping snapshot. Delayed invalidation
// refetches after writes settle; a failed refetch keeps the optimistic cache.
const LIST_READ_ATTEMPTS = 3;
const WRITE_WAIT_MS = 15_000;

/** Track a write from its start; call the result once it settles. */
function trackWrite(state: WriteState): (landed: boolean) => void {
  state.writesInFlight += 1;
  let settled = false;
  return (landed) => {
    if (settled) return;
    settled = true;
    state.writesInFlight -= 1;
    state.lastWriteSettledAt = ++state.clock;
    if (landed) state.lastWriteLandedAt = state.lastWriteSettledAt;
    if (state.writesInFlight === 0) {
      const waiters = [...state.waiters];
      waiters.forEach((resolve) => resolve());
    }
  };
}

function writesSettled(state: WriteState, signal: AbortSignal): Promise<void> {
  if (state.writesInFlight === 0 || signal.aborted) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      state.waiters.delete(done);
      signal.removeEventListener('abort', done);
      resolve();
    };
    const timer = setTimeout(done, WRITE_WAIT_MS);
    state.waiters.add(done);
    signal.addEventListener('abort', done, { once: true });
  });
}

/**
 * Inverse patches belong to the old list shape and can also undo another
 * write that succeeded. Keep the batch's starting inbox and replay its
 * successful and pending operations when one fails, even if refetch fails.
 */
function startWrite(
  dispatch: BaseQueryApi['dispatch'],
  apply: OptimisticWrite['apply']
): (landed: boolean) => void {
  const state = writeState(dispatch);
  const batch = (state.batch ??= { operations: [] });
  const operation = { apply, failed: false };
  batch.operations.push(operation);
  dispatch(
    notificationsApi.util.updateQueryData(
      'getNotifications',
      NOTIFICATIONS_QUERY_ARGS,
      (draft) => {
        batch.base ??= current(draft);
        apply(draft);
      }
    )
  );
  const settle = trackWrite(state);
  return (landed) => {
    try {
      if (!landed) {
        operation.failed = true;
        dispatch(
          notificationsApi.util.updateQueryData(
            'getNotifications',
            NOTIFICATIONS_QUERY_ARGS,
            () =>
              batch.base &&
              createNextState(batch.base, (draft) => {
                for (const op of batch.operations) {
                  if (!op.failed) op.apply(draft);
                }
              })
          )
        );
      }
    } finally {
      settle(landed);
      if (state.writesInFlight === 0) state.batch = undefined;
    }
  };
}

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Notifications', 'NotificationPreferences'],
  endpoints: (builder) => ({
    getNotificationPreferences: builder.query<NotificationPreferences, void>({
      query: () => '/notifications/preferences',
      providesTags: ['NotificationPreferences'],
    }),

    updateNotificationPreferences: builder.mutation<
      { message: string; preferences: NotificationPreferences },
      UpdateNotificationPreferencesRequest
    >({
      query: (preferences) => ({
        url: '/notifications/preferences',
        method: 'PATCH',
        body: preferences,
      }),
      invalidatesTags: ['NotificationPreferences'],
    }),

    getNotifications: builder.query<
      NotificationsListResponse,
      { unread_only?: boolean; limit?: number }
    >({
      async queryFn(
        { unread_only = false, limit = 50 },
        api,
        _extraOptions,
        baseQuery
      ) {
        const url = `/notifications/list?unread_only=${unread_only}&limit=${limit}`;
        const state = writeState(api.dispatch);
        const changed = {
          error: {
            status: 'CUSTOM_ERROR' as const,
            error: 'Notifications changed while loading',
          },
        };
        for (let attempt = 1; ; attempt++) {
          const startedAt = ++state.clock;
          const result = await baseQuery(url);
          if (result.error) return { error: result.error };
          const data = result.data as NotificationsListResponse;
          const overlapped =
            state.writesInFlight > 0 || state.lastWriteSettledAt > startedAt;
          if (!overlapped) return { data };
          await writesSettled(state, api.signal);
          if (api.signal.aborted || state.writesInFlight > 0) return changed;
          // Only a write that landed can have changed what this read saw.
          if (state.lastWriteLandedAt < startedAt) return { data };
          if (attempt === LIST_READ_ATTEMPTS) return changed;
        }
      },
      providesTags: ['Notifications'],
    }),

    markNotificationsRead: builder.mutation<
      MarkNotificationsReadResponse,
      MarkNotificationsReadRequest
    >({
      query: (request) => ({
        url: '/notifications/mark-read',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: ['Notifications'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const readAt = new Date().toISOString();
        const settle = startWrite(dispatch, (draft) => {
          const idsToMark = new Set(arg.notification_ids);
          let markedCount = 0;
          for (const n of draft.notifications) {
            if (idsToMark.has(n.id) && !n.is_read) {
              n.is_read = true;
              n.read_at = readAt;
              markedCount++;
            }
          }
          draft.unread_count = Math.max(0, draft.unread_count - markedCount);
        });
        try {
          await queryFulfilled;
          settle(true);
        } catch {
          settle(false);
          dispatch(notificationsApi.util.invalidateTags(['Notifications']));
        }
      },
    }),

    // The badge counts every unread notification, and the bell loads 20, so
    // the server marks them all (the loaded ids alone can't clear the count).
    markAllNotificationsRead: builder.mutation<
      MarkNotificationsReadResponse,
      void
    >({
      query: () => ({
        url: '/notifications/mark-all-read',
        method: 'POST',
      }),
      invalidatesTags: ['Notifications'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const readAt = new Date().toISOString();
        const settle = startWrite(dispatch, (draft) => {
          for (const n of draft.notifications) {
            if (!n.is_read) {
              n.is_read = true;
              n.read_at = readAt;
            }
          }
          draft.unread_count = 0;
        });
        try {
          await queryFulfilled;
          settle(true);
        } catch {
          settle(false);
          dispatch(notificationsApi.util.invalidateTags(['Notifications']));
        }
      },
    }),

    clearAllNotifications: builder.mutation<
      { message: string; deleted_count: number },
      void
    >({
      query: () => ({
        url: '/notifications/clear-all',
        method: 'DELETE',
      }),
      invalidatesTags: ['Notifications'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const settle = startWrite(dispatch, (draft) => {
          draft.notifications = [];
          draft.unread_count = 0;
        });
        try {
          await queryFulfilled;
          settle(true);
        } catch {
          settle(false);
          dispatch(notificationsApi.util.invalidateTags(['Notifications']));
        }
      },
    }),
  }),
});

export const {
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
  useGetNotificationsQuery,
  useMarkNotificationsReadMutation,
  useMarkAllNotificationsReadMutation,
  useClearAllNotificationsMutation,
} = notificationsApi;
